/**
 * Task #559 — Capability registry condiviso embedder per-tenant.
 *
 * Fonte unica di verità per filtri UI BE/FE sul cascade
 * "Foundation Embedder Provider". Estende sia il dropdown nuovo Sezione 0
 * Routing AI sia i filtri vision-capability sui modelli synthesis/rerank.
 *
 * Provider specs refer to Gemini Developer API, not Vertex.
 * Governed evidence/policy separation: google-model-governance.ts.
 * Storage estimates below are application estimates, not provider facts.
 */
import { GOOGLE_STANDARD_PRICES } from './google-pricing';

export type EmbedderProvider = 'openai' | 'gemini';

export interface EmbedderCapability {
  provider: EmbedderProvider;
  /** Model id passato all'API del provider */
  model: string;
  /** Display name umano per UI Routing AI */
  displayName: string;
  /** Dimensione vettore output (dopo MRL se applicabile) — deve corrispondere alla colonna pgvector */
  dim: number;
  /** Native dim del modello prima di MRL */
  nativeDim: number;
  /** Output dim minimo supportato via MRL (se applicabile) */
  mrlMinDim: number | null;
  /** Output dim massimo supportato via MRL */
  mrlMaxDim: number | null;
  /** True se il modello accetta input non-text (image/pdf/audio/video) nello stesso embedding space */
  multimodal: boolean;
  /** True se la pipeline ingestion deve generare image chunks separati per PDF/immagini */
  allowsImageChunks: boolean;
  /** True se gli image chunks vanno ri-allegati al synthesis come image_url (modello vision-capable richiesto) */
  requiresVisionSynthesis: boolean;
  /** True se il rerank multimodale (LLM-as-judge) deve essere vision-capable */
  requiresVisionRerank: boolean;
  /** Provider compatibili per query expansion / HyDE (allineamento spazio semantico) */
  compatibleQueryExpansionProviders: ReadonlyArray<'openai' | 'gemini' | 'claude'>;
  /** Max token input per singola request embedding */
  maxInputTokens: number;
  /** Max immagini per request (pertinente solo se multimodal=true) */
  maxImagesPerRequest: number | null;
  /** Max pagine PDF per request (pertinente solo se multimodal=true) */
  maxPdfPagesPerRequest: number | null;
  /** Max durata audio in secondi per request (pertinente solo se multimodal=true) */
  maxAudioDurationSec: number | null;
  /** Max durata video in secondi per request (pertinente solo se multimodal=true) */
  maxVideoDurationSec: number | null;
  /** Status del modello (GA / preview / experimental) */
  status: 'ga' | 'preview' | 'experimental';
  /** Data rilascio ufficiale GA (YYYY-MM-DD) */
  releaseDate: string | null;
  /** Pricing input testo $/1M tokens. null se non documentato pubblicamente — VIETATO stimare */
  pricingInputTextPer1M: number | null;
  /** Pricing input immagine $/1M tokens (o $/immagine, dipende dal provider). null se non documentato */
  pricingInputImagePer1M: number | null;
  /** Latenza tipica end-to-end attesa per single request, ms. null se non misurato */
  typicalLatencyMs: number | null;
  /** Rate limit di default richieste/minuto se documentato */
  defaultRpm: number | null;
  /** Nome colonna pgvector dove scrivere/leggere il vettore */
  embedColumn: 'embedding_hv' | 'embedding_gemini_hv';
  /** Documentazione URL per riferimento operatore */
  docsUrl: string;
  /**
   * Task #1237 — Stima byte medi DB consumati per documento ingerito.
   * Usata dal calcolatore di quota DB e dal master FE per warning
   * "Tenant <provider> su pacchetto <piano> sotto-dimensionato".
   *  - textOnly: documento solo testuale (chunking dual-level + embedding)
   *  - multimodal: documento con image/audio/video o PDF rasterizzato
   *    pagina-per-pagina (include vettore per pagina + media URL).
   * Stime conservative basate su ~6 KB/vettore (1536) o ~12 KB (3072) per
   * chunk + ~2 KB testo medio + indici HNSW proporzionati.
   */
  estimatedBytesPerDocument: {
    textOnly: number;
    multimodal: number;
  };
}

export const EMBEDDER_CAPABILITIES: Record<EmbedderProvider, EmbedderCapability> = {
  openai: {
    provider: 'openai',
    model: 'text-embedding-3-large',
    displayName: 'text-embedding-3-large',
    dim: 1536,
    nativeDim: 3072,
    mrlMinDim: 256,
    mrlMaxDim: 3072,
    multimodal: false,
    allowsImageChunks: false,
    requiresVisionSynthesis: false,
    requiresVisionRerank: false,
    compatibleQueryExpansionProviders: ['openai', 'claude'],
    maxInputTokens: 8191,
    maxImagesPerRequest: null,
    maxPdfPagesPerRequest: null,
    maxAudioDurationSec: null,
    maxVideoDurationSec: null,
    status: 'ga',
    releaseDate: '2024-01-25',
    pricingInputTextPer1M: 0.13,
    pricingInputImagePer1M: null,
    typicalLatencyMs: 250,
    defaultRpm: 5000,
    embedColumn: 'embedding_hv',
    docsUrl: 'https://platform.openai.com/docs/guides/embeddings',
    estimatedBytesPerDocument: {
      // ~30 child chunk @ 1536 dim (~6 KB vector) + ~2 KB testo + 1 parent
      // (no vector parent in legacy) → ~250 KB/doc + indice HNSW ~2× = ~500 KB.
      textOnly: 500_000,
      // OpenAI text-embedding-3-large è text-only, ma per coerenza UI espone
      // anche multimodal (uguale a textOnly + 0 → no overhead media).
      multimodal: 500_000,
    },
  },
  gemini: {
    provider: 'gemini',
    // Vertex AI / Gemini API model id ufficiale, GA 2026-04-22.
    // Nome esatto come pubblicato da Google — NON riformulare.
    model: 'gemini-embedding-2',
    displayName: 'gemini-embedding-2',
    dim: 3072,           // Existing halfvec storage; descriptive correction only.
    nativeDim: 3072,
    mrlMinDim: 128,
    mrlMaxDim: 3072,
    multimodal: true,
    allowsImageChunks: true,
    requiresVisionSynthesis: true,
    requiresVisionRerank: true,
    // Allineamento spazio semantico migliore con HyDE Gemini, ma OpenAI/Claude
    // testuali compatibili in fallback (testo viene riembeddato comunque da Gemini)
    compatibleQueryExpansionProviders: ['gemini', 'openai', 'claude'],
    maxInputTokens: 8192,
    maxImagesPerRequest: 6,
    maxPdfPagesPerRequest: 6,
    maxAudioDurationSec: 180,
    maxVideoDurationSec: 120,
    status: 'ga',
    releaseDate: '2026-04-22',
    pricingInputTextPer1M: GOOGLE_STANDARD_PRICES['gemini-embedding-2']?.input.TEXT ?? null,
    pricingInputImagePer1M: GOOGLE_STANDARD_PRICES['gemini-embedding-2']?.input.IMAGE ?? null,
    typicalLatencyMs: null,
    defaultRpm: null,
    embedColumn: 'embedding_gemini_hv',
    docsUrl: 'https://ai.google.dev/gemini-api/docs/embeddings?hl=en',
    estimatedBytesPerDocument: {
      // Text-only: ~30 chunk @ 3072 dim (~12 KB vector) + ~2 KB testo + parent
      // → ~450 KB/doc + halfvec HNSW (~6 KB × N) + indice ~2× = ~1.2 MB.
      textOnly: 1_200_000,
      // Multimodal: PDF tipico 20 pagine rasterizzato. 20 vettori 3072 dim
      // (~12 KB) + 20 chunk row + halfvec + indice HNSW + media URL meta
      // → ~5 MB/doc. NB: file binario PNG su R2, non DB.
      multimodal: 5_000_000,
    },
  },
};

/**
 * Default model id per provider embedder — FONTE UNICA DI VERITÀ.
 * Deriva direttamente dal registry capability: vietato ripetere il literal
 * del nome modello (`gemini-embedding-2` / `text-embedding-3-large`) sparso
 * nei service/route. Importare queste costanti invece di hardcodare la stringa.
 */
export const DEFAULT_GEMINI_EMBED_MODEL: string = EMBEDDER_CAPABILITIES.gemini.model;
export const DEFAULT_OPENAI_EMBED_MODEL: string = EMBEDDER_CAPABILITIES.openai.model;

/** Lista canonica dei provider embedder esposti nel cascade UI Sezione 0 */
export const EMBEDDER_PROVIDERS_ORDERED: ReadonlyArray<EmbedderProvider> = ['openai', 'gemini'];

/** Helper: ritorna la capability del provider, o lancia se sconosciuto */
export function getEmbedderCapability(provider: EmbedderProvider): EmbedderCapability {
  const cap = EMBEDDER_CAPABILITIES[provider];
  if (!cap) {
    throw new Error(`[EmbedderCapabilities] provider sconosciuto: ${provider}`);
  }
  return cap;
}

/** Helper: ritorna la colonna pgvector da usare per un embedder */
export function embedColumnFor(provider: EmbedderProvider): 'embedding_hv' | 'embedding_gemini_hv' {
  return EMBEDDER_CAPABILITIES[provider].embedColumn;
}

/**
 * Helper: dato un embedder, un service_key (rag_synthesis | rag_rerank_multimodal | ...)
 * e la lista cruda di modelli del provider, ritorna solo quelli compatibili
 * con il vincolo del cascade. Usato sia da BE (filtro server-side endpoint
 * /ai-tools/llm-models/:provider) sia da FE (validazione post-fetch).
 *
 * Nota: la regola di vision-capability (`requiresVisionSynthesis` /
 * `requiresVisionRerank`) viene applicata dal chiamante combinandola con
 * il regex/registry del catalog `ai_model_catalog`. Questo helper
 * espone solo il flag, non duplica la logica di capability detection.
 */
export interface CompatibilityResult<T> {
  compatible: T[];
  reason: string;
}

export function resolveCompatibleModels<T extends { id: string }>(
  embedder: EmbedderProvider,
  serviceKey: 'rag_synthesis' | 'rag_rerank' | 'rag_rerank_multimodal' | 'rag_query_expansion_model',
  allModels: T[],
  isVisionCapable: (modelId: string) => boolean,
): CompatibilityResult<T> {
  const cap = EMBEDDER_CAPABILITIES[embedder];

  switch (serviceKey) {
    case 'rag_synthesis':
      if (cap.requiresVisionSynthesis) {
        return {
          compatible: allModels.filter(m => isVisionCapable(m.id)),
          reason: 'embedder=gemini richiede synthesis vision-capable per ricevere image chunks',
        };
      }
      return { compatible: allModels, reason: 'embedder text-only: nessun filtro vision sul synthesis' };

    case 'rag_rerank_multimodal':
      // Esposto solo se embedder=gemini AND image_chunks attivo. Sempre vision.
      return {
        compatible: allModels.filter(m => isVisionCapable(m.id)),
        reason: 'rerank multimodale richiede modello vision-capable per scoring image chunks',
      };

    case 'rag_rerank':
      // Path text: nessun filtro vision (il rerank text-only resta valido anche
      // per tenant Gemini sui chunk text)
      return { compatible: allModels, reason: 'rerank text-only: nessun filtro vision' };

    case 'rag_query_expansion_model':
      // Filtro per provider compatibilità spazio semantico
      return { compatible: allModels, reason: 'query expansion: filtro provider applicato dal cascade dropdown' };

    default:
      return { compatible: allModels, reason: 'service_key non filtrato' };
  }
}
